import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppNav } from "@/components/AppNav";
import { useEffect, useState } from "react";
import { ArrowLeft, Bell, BarChart2, Star, KeyRound, Sun, Moon } from "lucide-react";
import { supabase, API_URL } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { useTheme } from "@/hooks/useTheme";

export const Route = createFileRoute("/minha-conta")({
  head: () => ({
    meta: [
      { title: "Minha conta — Busca Blindados" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MinhaContaPage,
});

function MinhaContaPage() {
  const { session, loading } = useSession();
  const { profile, isPaid, isExpired, alertLimit, hasAdvancedComparator, refresh } = useProfile();
  const navigate = useNavigate();
  const [activeAlertCount, setActiveAlertCount] = useState<number | null>(null);
  const [reactivating, setReactivating] = useState(false);
  const [reactivated, setReactivated] = useState(false);

  useEffect(() => {
    if (!loading && !session) navigate({ to: "/login" });
  }, [session, loading, navigate]);

  useEffect(() => {
    if (!session) return;
    supabase
      .from("filters")
      .select("id", { count: "exact", head: true })
      .eq("is_active", true)
      .is("deleted_at", null)
      .then(({ count }) => setActiveAlertCount(count ?? 0));
  }, [session]);

  if (loading) {
    return (
      <div className="min-h-screen text-foreground flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando sessão...</p>
      </div>
    );
  }

  if (!session) return null;

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const { data: { session: fresh } } = await supabase.auth.getSession();
      await fetch(`${API_URL}/api/reactivate-subscription`, {
        method: "POST",
        headers: { Authorization: `Bearer ${fresh?.access_token}` },
      });
      // Webhook customer.subscription.updated arrives within seconds and
      // syncSubscription unsets cancel_at_period_end. Refresh to pull it.
      setTimeout(() => refresh(), 1500);
      setReactivated(true);
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="min-h-screen text-foreground">
      <AppNav />

      <main className="mx-auto max-w-6xl px-6 pb-32 pt-10">
        <button
          onClick={() => window.history.back()}
          className="mb-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Voltar
        </button>

        <h1 className="font-display text-3xl text-foreground sm:text-4xl">Minha conta</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {profile?.full_name && <span>{profile.full_name} · </span>}
          {session.user.email}
        </p>

        <div className="mt-10 space-y-4">
          {/* Plan card */}
          <section className="surface-card rounded-2xl p-6">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-4">
              Plano
            </h2>

            {reactivated && (
              <div className="mb-4 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400">
                Plano reativado! Seu acesso continua normalmente até o fim do período.
              </div>
            )}

            {isPaid && profile?.plan ? (
              <div className="flex items-start justify-between gap-4">
                <span className="font-display text-lg text-foreground">
                  {profile.plan.display_name}
                </span>
                <div className="flex flex-col items-end gap-1.5">
                  {profile.subscription_status === "cancelled" ? (
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-400">
                      Cancelado
                    </span>
                  ) : (
                    <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-400">
                      Ativo
                    </span>
                  )}
                  {profile.plan_expires_at && (
                    <p className="text-right text-xs text-muted-foreground">
                      {profile.cancel_at_period_end ? "Acesso até " : "Próxima cobrança em "}
                      <span className="text-foreground">
                        {new Date(profile.plan_expires_at).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            ) : isExpired ? (
              <div className="flex items-center justify-between">
                <span className="font-display text-lg text-foreground">
                  {profile?.plan?.display_name ?? "Plano expirado"}
                </span>
                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-400">
                  Expirado
                </span>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Você ainda não tem um plano ativo.
                </p>
                <Link
                  to="/planos"
                  className="btn-primary inline-flex rounded-full px-5 py-2.5 text-sm font-medium"
                >
                  Ver planos
                </Link>
              </div>
            )}

            {(isPaid || isExpired) && (
              <div className="mt-4 flex items-center justify-between border-t dark:border-white/5 border-black/5 pt-4">
                <Link
                  to="/planos"
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  {isExpired ? "Renovar plano →" : profile?.plan_id === "compra" ? "Fazer Upgrade →" : "Ver todos os planos →"}
                </Link>
                {isPaid && (
                  profile?.subscription_status === "cancelled" ? (
                    <button
                      onClick={handleReactivate}
                      disabled={reactivating}
                      className="text-xs text-primary/80 transition-colors hover:text-primary disabled:opacity-50"
                    >
                      {reactivating ? "Reativando..." : "Reativar plano"}
                    </button>
                  ) : (
                    <Link
                      to="/cancelar"
                      className="rounded-full px-2.5 py-1 text-xs text-red-400/80 transition-all hover:bg-red-500 hover:text-white"
                    >
                      Cancelar plano
                    </Link>
                  )
                )}
              </div>
            )}
          </section>

          {/* Password card */}
          <PasswordSection />

          {/* Aparência card */}
          <section className="surface-card rounded-2xl p-6">
            <h2 className="text-sm font-semibold text-foreground">Aparência</h2>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground">Tema</p>
                <p className="text-xs text-muted-foreground">Claro ou escuro — salvo no dispositivo.</p>
              </div>
              <ThemeToggleRow />
            </div>
          </section>

          {/* Notificações card */}
          <section className="surface-card rounded-2xl p-6">
            <h2 className="text-sm font-semibold text-foreground">Notificações</h2>
            <div className="mt-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground">Resumo semanal</p>
                <p className="text-xs text-muted-foreground">
                  Um giro aos sábados com o que mexeu nos seus alertas: novos, quedas de preço e mediana da semana.
                </p>
              </div>
              <DigestToggleRow
                enabled={profile?.weekly_digest_enabled ?? true}
                onChange={refresh}
              />
            </div>
          </section>

          {/* Features card */}
          {isPaid && (
            <section className="surface-card rounded-2xl p-6">
              <h2 className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-4">
                Recursos incluídos
              </h2>
              <ul className="space-y-3">
                <FeatureRow
                  icon={<Bell className="h-4 w-4" />}
                  label="Alertas inteligentes"
                  value={
                    activeAlertCount !== null && alertLimit > 0
                      ? `${activeAlertCount} / ${alertLimit} ativos`
                      : `Até ${alertLimit}`
                  }
                  action={<Link to="/alertas" className="text-xs text-primary hover:underline">Gerenciar</Link>}
                />
                <FeatureRow
                  icon={<BarChart2 className="h-4 w-4" />}
                  label="Comparador avançado"
                  value={hasAdvancedComparator ? "Incluído" : "Não incluído"}
                  muted={!hasAdvancedComparator}
                />
                <FeatureRow
                  icon={<Star className="h-4 w-4" />}
                  label="Favoritos"
                  value="Incluído"
                />
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}

function PasswordSection() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setErrMsg("A senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setErrMsg("As senhas não conferem.");
      return;
    }
    setSaving(true);
    setErrMsg(null);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) {
      setStatus("error");
      setErrMsg(error.message ?? "Não foi possível salvar a senha.");
      return;
    }
    setStatus("saved");
    setPassword("");
    setConfirm("");
    setOpen(false);
  };

  return (
    <section className="surface-card rounded-2xl p-6">
      <h2 className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-4">
        Senha
      </h2>
      {status === "saved" && (
        <p className="mb-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
          Senha atualizada. Você pode entrar diretamente sem o link mágico.
        </p>
      )}
      {!open ? (
        <div className="flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2.5 text-muted-foreground">
            <KeyRound className="h-4 w-4" />
            <span>Definir ou atualizar senha</span>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(true); setStatus("idle"); }}
            className="text-xs text-primary hover:underline"
          >
            Definir
          </button>
        </div>
      ) : (
        <form onSubmit={save} className="space-y-3">
          <label className="block">
            <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Nova senha
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/60"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Confirmar senha
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/60"
            />
          </label>
          {errMsg && (
            <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {errMsg}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={saving || !password || !confirm}
              className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Salvando..." : "Salvar senha"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setPassword(""); setConfirm(""); setErrMsg(null); }}
              className="rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-4 py-2.5 text-sm text-foreground/80 dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function ThemeToggleRow() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="flex items-center gap-1 rounded-full dark:bg-white/[0.06] bg-black/[0.04] p-1">
      <button
        type="button"
        onClick={() => setTheme("light")}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${theme === "light" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
      >
        <Sun className="h-3.5 w-3.5" /> Claro
      </button>
      <button
        type="button"
        onClick={() => setTheme("dark")}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${theme === "dark" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
      >
        <Moon className="h-3.5 w-3.5" /> Escuro
      </button>
    </div>
  );
}

function DigestToggleRow({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  const [optimistic, setOptimistic] = useState(enabled);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOptimistic(enabled);
  }, [enabled]);

  const toggle = async () => {
    const next = !optimistic;
    setOptimistic(next);
    setSaving(true);
    const { error } = await supabase.rpc("set_weekly_digest_enabled", { p_enabled: next });
    setSaving(false);
    if (error) {
      setOptimistic(!next); // revert on failure
      return;
    }
    onChange();
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={optimistic}
      aria-label="Resumo semanal por email"
      onClick={toggle}
      disabled={saving}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${optimistic ? "bg-primary" : "dark:bg-white/[0.12] bg-black/[0.12]"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${optimistic ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

function FeatureRow({
  icon,
  label,
  value,
  muted = false,
  action,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  muted?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <li className="flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2.5 text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={muted ? "text-muted-foreground/50" : "text-foreground/90"}>{value}</span>
        {action}
      </div>
    </li>
  );
}
